import './TemplateSelector.scss';
import React, { useCallback } from 'react';
import { FlightButton, FlightSelect } from '@flybits/design-system';
import { ReactComponent as IconSendGrid } from 'assets/icons/icon-sendgrid.svg';
import { ReactComponent as IconTwilio } from 'assets/icons/icon-twilio.svg';
import { TEmailProviderTemplate, TSMSProviderTemplate } from 'components/FlowBuilder/FlowSidePanels/types';

const MAIN_CLASS = 'template-selector';
const CLASSES = {
  CONTAINER: `${MAIN_CLASS}__container`,
  HEADER: `${MAIN_CLASS}__container__header`,
  HEADER_ICON: `${MAIN_CLASS}__container__header__icon`,
  HEADER_TITLE: `${MAIN_CLASS}__container__header__title`,
  HEADER_TITLE_RED: `${MAIN_CLASS}__container__header__title--red`,
  SELECTOR: `${MAIN_CLASS}__container__selector`,
  SELECTOR_DROPDOWN: `${MAIN_CLASS}__container__selector__dropdown`,
  REFRESH_BUTTON: `${MAIN_CLASS}__container__selector__refresh`,
  FOOTER: `${MAIN_CLASS}__container__footer`,
};

interface TemplateSelectorProps {
  type: 'email' | 'sms';
  templates: TEmailProviderTemplate[] | TSMSProviderTemplate[];
  isTemplateError?: boolean;
  selectedTemplate?: TEmailProviderTemplate | TSMSProviderTemplate | null;
  onTemplateSelect: (template: TEmailProviderTemplate | TSMSProviderTemplate) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

const EMPTY_TEMPLATES_MESSAGE = {
  email: 'No SendGrid templates available. Please add templates in SendGrid console.',
  sms: 'No Twilio templates available. Please add templates in Twilio console.',
};

const FETCH_ERROR_MESSAGE = {
  email: 'Unable to fetch templates, please try again later.',
  sms: "We couldn't retrieve templates at the moment. Please refresh the page or try again later.",
};

function TemplateSelector({
  type,
  onTemplateSelect,
  templates,
  isTemplateError,
  selectedTemplate,
  onRefresh,
  isRefreshing = false,
}: TemplateSelectorProps) {
  const handleSelectTemplate = useCallback(
    (template: TEmailProviderTemplate | TSMSProviderTemplate) => {
      onTemplateSelect(template);
    },
    [onTemplateSelect],
  );

  const isEmptyTemplates = templates.length === 0;
  const hasError = !!isTemplateError || (isEmptyTemplates && !isRefreshing);
  const errorMessage = hasError ? (isTemplateError ? FETCH_ERROR_MESSAGE[type] : EMPTY_TEMPLATES_MESSAGE[type]) : '';

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.CONTAINER}>
        <div className={CLASSES.HEADER}>
          <div className={CLASSES.HEADER_ICON}>{type === 'email' ? <IconSendGrid /> : <IconTwilio />}</div>
          <div className={CLASSES.HEADER_TITLE}>
            {type === 'email' ? 'SendGrid' : 'SMS'} template
            <span className={CLASSES.HEADER_TITLE_RED}>*</span>
          </div>
        </div>
        <div className={CLASSES.SELECTOR}>
          <div className={CLASSES.SELECTOR_DROPDOWN}>
            <FlightSelect
              ariaControlsId="template-selector"
              options={templates}
              selected={selectedTemplate}
              handleOptionClick={handleSelectTemplate}
              hasError={hasError}
              disabled={isRefreshing}
              errorMessage={errorMessage}
            />
          </div>
          {onRefresh && (
            <FlightButton
              className={CLASSES.REFRESH_BUTTON}
              aria-label="Refresh templates"
              iconLeft="replay"
              theme="secondary"
              size="small"
              onClick={() => onRefresh()}
              disabled={isRefreshing}
            />
          )}
        </div>
        {!hasError && <div className={CLASSES.FOOTER}>The template will define how your {type} will look</div>}
      </div>
    </div>
  );
}

export default TemplateSelector;
